import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import AvatarUpload from "./AvatarUpload";
import FieldSection from "./FieldSection";
import { useFileUpload } from "hooks/useFileUpload";
import useUsers from "hooks/userManagement/useUsers";
import { STORAGE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const identityFields = [
  "username",
  "email",
  "phone",
  "firstName",
  "middleName",
  "lastName",
  "status",
];

export default function UserPersonalInfo({ user }) {
  const [previewPhoto, setPreviewPhoto] = useState(user.profilePhoto || null);
  const { uploadFile, getFileMetadata } = useFileUpload();
  const { patchProfileProperties } = useUsers();

  useEffect(() => {
    return () => {
      if (previewPhoto?.startsWith("blob:")) {
        URL.revokeObjectURL(previewPhoto);
      }
    };
  }, [previewPhoto]);

  // Convert display name to API field name (camelCase)
  const mapFieldName = (displayName) =>
    displayName
      .split(" ")
      .map((word, idx) =>
        idx === 0
          ? word.charAt(0).toLowerCase() + word.slice(1)
          : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join("");

  const isIdentityField = (fieldKey) => identityFields.includes(fieldKey);

  const updateIdentityFields = async (updatedFields) => {
    try {
      const authToken = sessionStorage.getItem("authToken");
      if (!authToken) throw new Error("No auth token");

      const payload = Object.entries(updatedFields).map(([key, value]) => ({
        name: key,
        value,
      }));

      await apiClient.patch(
        `/identity-service/api/v1/users/props?uid=${user.id}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      Object.entries(updatedFields).forEach(([key, value]) => {
        user[key] = value;
      });

      toast.success("Identity info updated successfully");
      return true;
    } catch (err) {
      console.error("Identity update failed:", err.response?.data || err.message);
      toast.error("Failed to update identity information.");
      return false;
    }
  };

  const updateProfileFields = async (updatedFields) => {
    try {
      const authToken = sessionStorage.getItem("authToken");
      if (!authToken) throw new Error("No auth token");

      const payload = Object.entries(updatedFields).map(([key, value]) => ({
        name: key,
        value,
      }));

      await apiClient.patch(
        `/profile-service/api/v1/user-profiles/props?uid=${user.id}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      Object.entries(updatedFields).forEach(([key, value]) => {
        user[key] = value;
      });

      toast.success("Profile info updated successfully");
      return true;
    } catch (err) {
      console.error("Profile update failed:", err.response?.data || err.message);
      toast.error("Failed to update profile information.");
      return false;
    }
  };

  // Save single field at a time
  const handleFieldSave = async (updatedFields) => {
    // updatedFields has one key, e.g. { "First Name": "Marvin" }
    const mappedFields = {};
    Object.entries(updatedFields).forEach(([key, value]) => {
      mappedFields[mapFieldName(key)] = value;
    });

    const key = Object.keys(mappedFields)[0];
    const value = mappedFields[key];

    if (isIdentityField(key)) {
      return await updateIdentityFields({ [key]: value });
    } else {
      return await updateProfileFields({ [key]: value });
    }
  };

  // Photo upload handler
  const handlePhotoUpload = async (file) => {
    try {
      toast.info("Uploading photo...");
      const result = await uploadFile(user.id, file, {
        purpose: "USER_PROFILE",
        fileType: "PHOTO",
      });

      await patchProfileProperties(user.id, [{ name: "profilePhoto", value: result.fileId }]);

      const metadata = await getFileMetadata(result.fileId);
      const photoUrl = metadata?.url || `${STORAGE_BASE_URL}/files/${result.fileId}`;

      setPreviewPhoto(photoUrl);
      user.profilePhoto = photoUrl;

      toast.success("Profile photo updated successfully");
    } catch (err) {
      console.error("Photo upload failed:", err);
      toast.error("Failed to upload photo. Please try again.");
    }
  };

  // Helper to create fields object with editable true
  const singleField = (label, value, type = "text") => ({
    [label]: { value, type, editable: true },
  });

  return (
    <>
      <h6 className="fw-bold text-sm">
        <FontAwesomeIcon icon="camera-retro" className="me-2" />
        Profile photo
      </h6>
      <div className="d-flex align-items-center justify-content-center my-4">
        <AvatarUpload
          src={previewPhoto}
          onChange={async (file) => {
            const previewURL = URL.createObjectURL(file);
            setPreviewPhoto(previewURL);
            await handlePhotoUpload(file);
          }}
        />
      </div>

      {/* Separate editable fields */}
      <FieldSection
        title="First Name"
        icon="id-card"
        description="Edit your first name."
        fields={singleField("First Name", user.firstName)}
        onSave={handleFieldSave}
      />
      <FieldSection
        title="Last Name"
        icon="id-card"
        description="Edit your last name."
        fields={singleField("Last Name", user.lastName)}
        onSave={handleFieldSave}
      />
      <FieldSection
        title="Middle Name"
        icon="id-card"
        description="Edit your middle name."
        fields={singleField("Middle Name", user.middleName)}
        onSave={handleFieldSave}
      />

      <FieldSection
        title="Username"
        icon="user"
        description="Your username is displayed publicly and cannot be changed."
        fields={{ Username: { value: user.username, type: "text", editable: false } }}
      />

      <FieldSection
        title="Gender"
        icon="venus-mars"
        description="Adjust your gender to ensure it’s accurate in your account."
        fields={singleField("Gender", user.gender)}
        onSave={handleFieldSave}
      />

      <FieldSection
        title="Birthday"
        icon="birthday-cake"
        description="Adjust your date of birth to ensure it's accurate in your account."
        fields={singleField("Birth Date", user.birthDate, "date")}
        onSave={handleFieldSave}
      />

      <FieldSection
        title="Email"
        icon="envelope"
        description="Your primary email address for notifications and account security."
        fields={singleField("Email", user.email, "email")}
        onSave={handleFieldSave}
      />

      <FieldSection
        title="Phone"
        icon="phone-alt"
        description="Add a personal or official phone number to stay connected with ease."
        fields={singleField("Phone", user.phone, "tel")}
        onSave={handleFieldSave}
      />

      {/* Uncomment if needed */}
      {/*
      <FieldSection
        title="Next of Kin"
        icon="male"
        description="Update your next of kin to ensure it’s accurate in your account."
        fields={singleField("Next of Kin", user.nextOfKin)}
        onSave={handleFieldSave}
      />
      */}
    </>
  );
}
